import React, { useState, useEffect } from "react";
import { Card, CardContent, Typography, Divider, FormControl, InputLabel, Select, MenuItem, Box } from "@mui/material";
import { BarChart, BarPlot, axisClasses } from "@mui/x-charts";
import LeaderboardIcon from "@mui/icons-material/Leaderboard";
import GroupIcon from "@mui/icons-material/Group";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import HandshakeIcon from "@mui/icons-material/Handshake";
import { getDashboardData, DashboardData } from "../../../services/business.service";
import Loader from "../../../common/loader";

const TIME_RANGES = [
  { value: 1, label: "1 Month" },
  { value: 3, label: "3 Months" },
  { value: 6, label: "6 Months" },
];

const DEFAULT_DASHBOARD_DATA: DashboardData = {
  totalLeads: 0,
  totalProspects: 0,
  totalCustomers: 0,
  totalDeals: 0,
};

const Dashboard: React.FC = () => {
  const [data, setData] = useState<DashboardData>(DEFAULT_DASHBOARD_DATA);
  const [funnelData, setFunnelData] = useState([
    { name: "Leads", count: 0 },
    { name: "Prospects", count: 0 },
    { name: "Customers", count: 0 },
    { name: "Deals", count: 0 },
  ]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<number>(3);

  useEffect(() => {
    const fetchDashboardData = async () => {
        setLoading(true);
        try {
          const count = await getDashboardData(timeRange);
          if (count && typeof count === 'object' && 'totalLeads' in count) {
            setData(count);
            setFunnelData([
              { name: "Leads", count: count.totalLeads },
              { name: "Prospects", count: count.totalProspects },
              { name: "Customers", count: count.totalCustomers },
              { name: "Deals", count: count.totalDeals },
            ]);
          } else {
            setData(DEFAULT_DASHBOARD_DATA);
          }
        } catch (error) {
          console.error("Failed to fetch dashboard data:", error);
        } finally {
          setLoading(false);
        }
    };

    fetchDashboardData();
  }, [timeRange]);

  if(loading){
    return <Loader></Loader>
  }
  return (
    <div className="p-2 bg-gray-50 min-h-screen">
      {/* Time Filter */}
      <Box className="flex justify-center mb-4">
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="time-range-label">Time Range</InputLabel>
          <Select
            value={timeRange}
            labelId="time-range-label"
            label="Time Range"
            onChange={(e) => setTimeRange(Number(e.target.value))}
            sx={{
              bgcolor: 'white',
              borderRadius: 2,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#e2e8f0',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#1976d2',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#1976d2',
                borderWidth: 2,
              },
            }}
          >
            {TIME_RANGES.map((range) => (
              <MenuItem 
                key={range.value} 
                value={range.value}
                sx={{
                  '&:hover': {
                    bgcolor: '#f3f4f6',
                  },
                  '&.Mui-selected': {
                    bgcolor: '#e3f2fd',
                    '&:hover': {
                      bgcolor: '#bbdefb',
                    },
                  },
                }}
              >
                {range.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* KPI Summary Row */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <Card
          className="shadow-md rounded-2xl border border-gray-100 bg-gradient-to-r from-blue-50 to-blue-100"
          sx={{ borderRadius: 3 }}
        >
          <CardContent className="flex flex-col items-center justify-center p-6">
            <PersonAddAltIcon className="text-blue-600 mb-2" fontSize="large" />
            <Typography variant="h6" className="font-bold text-gray-800">
              Leads Created
            </Typography>
            <Typography variant="h5" className="font-extrabold text-blue-700">
              {loading ? "..." : data.totalLeads}
            </Typography>
          </CardContent>
        </Card>

        <Card
          className="shadow-md rounded-2xl border border-gray-100 bg-gradient-to-r from-yellow-50 to-yellow-100"
          sx={{ borderRadius: 3 }}
        >
          <CardContent className="flex flex-col items-center justify-center p-6">
            <TrendingUpIcon className="text-yellow-600 mb-2" fontSize="large" />
            <Typography variant="h6" className="font-bold text-gray-800">
              Prospects Converted
            </Typography>
            <Typography variant="h5" className="font-extrabold text-yellow-700">
              {loading ? "..." : data.totalProspects}
            </Typography>
          </CardContent>
        </Card>

        <Card
          className="shadow-md rounded-2xl border border-gray-100 bg-gradient-to-r from-green-50 to-green-100"
          sx={{ borderRadius: 3 }}
        >
          <CardContent className="flex flex-col items-center justify-center p-6">
            <GroupIcon className="text-green-600 mb-2" fontSize="large" />
            <Typography variant="h6" className="font-bold text-gray-800">
              Customers Onboarded
            </Typography>
            <Typography variant="h5" className="font-extrabold text-green-700">
              {loading ? "..." : data.totalCustomers}
            </Typography>
          </CardContent>
        </Card>

        <Card
          className="shadow-md rounded-2xl border border-gray-100 bg-gradient-to-r from-yellow-50 to-yellow-100"
          sx={{ borderRadius: 3 }}
        >
          <CardContent className="flex flex-col items-center justify-center p-6">
            <HandshakeIcon className="text-yellow-600 mb-2" fontSize="large" />
            <Typography variant="h6" className="font-bold text-gray-800">
              Deals Created
            </Typography>
            <Typography variant="h5" className="font-extrabold text-yellow-700">
              {loading ? "..." : data.totalDeals}
            </Typography>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 gap-6">
        {/* Funnel Chart */}
        <Card
          className="shadow-lg rounded-2xl border border-gray-100 hover:shadow-2xl transition-shadow duration-300"
          sx={{ borderRadius: 3 }}
        >
          <CardContent>
            <div className="flex items-center gap-2 mb-3">
              <LeaderboardIcon className="text-blue-600" />
              <Typography variant="h6" className="font-bold text-gray-800">
                Sales Funnel Overview
              </Typography>
            </div>
            <Divider className="mb-4" />
            <BarChart
              xAxis={[
                { scaleType: "band", data: funnelData.map((d) => d.name) },
              ]}
              series={[
                {
                  data: funnelData.map((d) => d.count),
                  color: "#1976d2",
                  label: "Count",
                },
              ]}
              height={250}
              margin={{ top: 6, right: 6, bottom: 6, left: 6 }}
              sx={{
                [`& .${axisClasses.left} .${axisClasses.label}`]: {
                  transform: "translate(-20px, 0)",
                },
              }}
            >
              <BarPlot />
            </BarChart>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
